import { type ReactNode } from 'react';
import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import type { BaseDrawerProps } from '@/types';

interface BaseDrawerWrapperProps extends BaseDrawerProps {
  children?: ReactNode;
  footer?: ReactNode;
  title?: string;
  /** Подзаголовок в хедере drawer'а */
  description?: string;
  /** Показать заголовок видимым в DrawerHeader (по умолчанию скрыт для a11y) */
  showTitle?: boolean;
  /** Кастомный className для DrawerContent */
  className?: string;
}

/**
 * Базовый компонент drawer (выдвижная панель снизу).
 * Обертка над shadcn Drawer с предустановленными настройками:
 * - Скрытый/видимый заголовок для accessibility
 * - Опциональный footer с нижним отступом 40px
 * - Контролируемое состояние open/onOpenChange
 */
export function BaseDrawer({ 
  open, 
  onOpenChange, 
  children, 
  footer,
  title = "Drawer",
  description,
  showTitle = false,
  className,
}: BaseDrawerWrapperProps): JSX.Element {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={className}>
        {showTitle ? (
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            {description && (
              <DrawerDescription>{description}</DrawerDescription>
            )}
          </DrawerHeader>
        ) : (
          <VisuallyHiddenPrimitive.Root>
            <DrawerTitle>{title}</DrawerTitle>
          </VisuallyHiddenPrimitive.Root>
        )}
        {children}
        
        {footer ? (
          <DrawerFooter>{footer}</DrawerFooter>
        ) : (
          /* Нижний отступ 40px для drawer'ов без footer (аналог pb-10 в DrawerFooter) */
          <div className="pb-10" />
        )}
      </DrawerContent>
    </Drawer>
  );
}